// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
// The Hasura role of each signing widget call. Hasura serves an action only to
// the roles its permissions list (hasura/metadata/actions.yaml) and refuses a
// role the user doesn't hold, so the portal's default role is no good for
// staff without admin-user. Harvest authorizes from the JWT itself.
import { IPermissions } from "@/types/keycloak";
import { SIGNING_ACTIONS, SigningAction } from "./types";
/** The widget's calls, grouped by the roles their Hasura action allows. */
export var SigningOperation;
(function (SigningOperation) {
    /** signingGetRequest: admin-user, every sign-<action> and signing-requests-read. */
    SigningOperation["GetRequest"] = "get-request";
    /**
     * signingCheckCertificate, signingPdfPrepare, signingApprove, signingOpenFailure and
     * signingHandover: admin-user and the sign-<action> permissions.
     */
    SigningOperation["Sign"] = "sign";
    /**
     * signingCancel: admin-user, signing-requests-cancel, the sign-<action> permissions and
     * the permissions that start an action (a requester cancels their own request;
     * Harvest checks they started it).
     */
    SigningOperation["Cancel"] = "cancel";
})(SigningOperation || (SigningOperation = {}));
var SIGN_PERMISSIONS = Object.values(SIGNING_ACTIONS).map(function (info) { return info.signPermission; });
/**
 * The permission of the route that starts each action, which its requester holds:
 * Initialize voting and the election returns come from the tally, Open and Close
 * voting from the Post's status, a report from its generation, a transmission
 * from its package, a voter from the application's decision, a configuration
 * version from Publish, and the trustee steps from the ceremonies.
 */
var START_PERMISSIONS = (_a = {},
    _a[SigningAction.InitializeVoting] = IPermissions.ADMIN_CEREMONY,
    _a[SigningAction.OpenVoting] = IPermissions.ELECTION_STATE_WRITE,
    _a[SigningAction.CloseVoting] = IPermissions.ELECTION_STATE_WRITE,
    _a[SigningAction.GenerateElectionReturns] = IPermissions.ADMIN_CEREMONY,
    _a[SigningAction.GenerateReports] = IPermissions.REPORT_READ,
    _a[SigningAction.TransmitResults] = IPermissions.MIRU_CREATE,
    _a[SigningAction.ApproveVoter] = IPermissions.APPLICATION_WRITE,
    _a[SigningAction.ApproveConfiguration] = IPermissions.PUBLISH_WRITE,
    _a[SigningAction.ConfirmCustodyProof] = IPermissions.TRUSTEE_CEREMONY,
    _a[SigningAction.ContributeCustodyProof] = IPermissions.TRUSTEE_CEREMONY,
    _a);
/**
 * The role to send: one the operation's action allows and the user holds, the
 * request's own sign permission first. `null` when the user holds none.
 */
export var signingOperationRole = function (operation, action, holds) {
    var _a;
    var held = function () {
        var _a;
        var candidates = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            candidates[_i] = arguments[_i];
        }
        return (_a = candidates.find(function (candidate) { return candidate !== undefined && holds(candidate); })) !== null && _a !== void 0 ? _a : null;
    };
    var ownSign = action ? (_a = SIGNING_ACTIONS[action]) === null || _a === void 0 ? void 0 : _a.signPermission : undefined;
    switch (operation) {
        case SigningOperation.Cancel:
            return held(IPermissions.SIGNING_REQUESTS_CANCEL, ownSign, IPermissions.ADMIN_USER, action ? START_PERMISSIONS[action] : undefined);
        case SigningOperation.GetRequest:
            return held.apply(void 0, __spreadArray([ownSign,
                IPermissions.SIGNING_REQUESTS_READ,
                IPermissions.ADMIN_USER], SIGN_PERMISSIONS, false));
        case SigningOperation.Sign:
            return held.apply(void 0, __spreadArray([ownSign, IPermissions.ADMIN_USER], SIGN_PERMISSIONS, false));
    }
};
/** The comparison query reads subjects only for configuration signers or request readers. */
export var configurationApprovalRole = function (holds) {
    var _a;
    return (_a = [
        IPermissions.SIGN_APPROVE_CONFIGURATION,
        IPermissions.SIGNING_REQUESTS_READ,
        IPermissions.ADMIN_USER,
    ].find(holds)) !== null && _a !== void 0 ? _a : null;
};
